import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Card, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { patchRequest, trackingShipmentUrl } from '../../config'
import {
  NOTIFY_STATUS_COLORS,
  formatDateTime,
  notifyLabel,
  notifyReason,
  updatesSummary
} from './utils'

// What the customer has been told about this shipment, and whether we are still telling them
// (spec: whatsapp-shipment-tracking Req 2.1).
//
// Until the updates existed, nobody was told anything: every alert went to the team on Telegram
// and the person who paid had to open the app and look. This panel is the other side of that —
// the team can now see the messages the customer actually got, and the reason for every one that
// did not go out.
//
// The switch is here because most customers ask on the phone rather than by replying STOP, and an
// operator who cannot honour that has to tell somebody to go and text a word. Every flip is
// recorded in the action log with the operator's name: "who stopped this customer hearing from
// us, and when" is a question that will be asked.
const UpdatesPanel = ({ shipment, onChanged }) => {
  const row = shipment || {}
  const [saving, setSaving] = useState(false)
  const summary = updatesSummary(row)
  const rows = row.notifications || []

  const toggle = async checked => {
    const response = await patchRequest(
      trackingShipmentUrl(row.id),
      { whatsappUpdates: checked },
      'WhatsApp updates',
      setSaving
    )
    // Re-read rather than patch a local copy: the action log gained a line too, and what is on
    // screen should be what the server holds.
    if (response.success && onChanged) onChanged()
  }

  return (
    <Card
      title="WhatsApp updates"
      extra={
        <Space>
          <Tag color={summary.color}>{summary.label}</Tag>
          <Tooltip title="Switch the shipment updates we send to this customer's number on or off.">
            <Switch
              checked={!!row.whatsappUpdates}
              loading={saving}
              disabled={!row.id}
              onChange={toggle}
            />
          </Tooltip>
        </Space>
      }
    >
      {/* Said out loud, because the two are different facts and only one of them is a complaint:
          a customer who replied STOP has told us something, and a shipment whose updates were
          never on has not. */}
      {!row.whatsappUpdates && row.updatesStoppedAt ? (
        <div style={{ marginBottom: 12 }}>
          {`The customer asked us to stop on ${formatDateTime(row.updatesStoppedAt)}.`}
        </div>
      ) : null}

      {/* The column renders are arrows inside an antd `render`, which trips react/display-name;
          `shipmentColumns.js` disables it for the same reason. */}
      {/* eslint-disable react/display-name */}
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        pagination={false}
        locale={{
          emptyText: row.paid
            ? 'Nothing has happened to this shipment yet, so nothing has been sent.'
            : 'Updates start once the shipment is paid for.'
        }}
        columns={[
          {
            title: 'Update',
            key: 'kind',
            render: (_, r) => (
              <div>
                <div>{notifyLabel(r)}</div>
                {/* The key is what makes a kind repeatable where it should be: the stage, the
                    arrival date, the alert. */}
                <div style={{ opacity: 0.65 }}>{r.key || '—'}</div>
              </div>
            )
          },
          {
            title: 'Status',
            key: 'status',
            render: (_, r) => (
              <div>
                <Tag color={NOTIFY_STATUS_COLORS[r.status] || 'default'}>{r.status}</Tag>
                {r.attempts > 1 ? (
                  <div style={{ opacity: 0.65 }}>{`${r.attempts} attempts`}</div>
                ) : null}
              </div>
            )
          },
          {
            title: 'Why not',
            key: 'reason',
            render: (_, r) => notifyReason(r) || '—'
          },
          {
            title: 'When',
            key: 'when',
            render: (_, r) => (
              <div>
                <div>{formatDateTime(r.sentAt || r.createdAt)}</div>
                {r.waMessageId ? (
                  <Typography.Text style={{ opacity: 0.65 }} copyable>
                    {r.waMessageId}
                  </Typography.Text>
                ) : null}
              </div>
            )
          }
        ]}
      />
      {/* eslint-enable react/display-name */}
    </Card>
  )
}

UpdatesPanel.propTypes = {
  shipment: PropTypes.object,
  onChanged: PropTypes.func
}

export default UpdatesPanel
